if (!customElements.get('dongxi-home-slideshow')) {
  customElements.define('dongxi-home-slideshow', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-story-slide]')];
      this.track = this.querySelector('.dx-stories__slides');
      this.tabs = [...this.querySelectorAll('[data-story-tab]')];
      this.toggle = this.querySelector('[data-story-toggle]');
      if (this.slides.length < 2) return;
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.firstImageReady = false;
      this.userPaused = false;
      this.hovered = false;
      this.onScreen = true;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-story-controls]').hidden = false;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.goTo(index, true), options);
      });
      this.querySelector('[data-story-previous]').addEventListener('click', () => this.goTo(this.index - 1, true), options);
      this.querySelector('[data-story-next]').addEventListener('click', () => this.goTo(this.index + 1, true), options);
      this.toggle.addEventListener('pointerdown', () => { this.toggleIntent = !this.userPaused; }, options);
      this.toggle.addEventListener('click', () => {
        this.userPaused = this.toggleIntent ?? !this.userPaused;
        this.toggleIntent = undefined;
        this.updatePlayback();
      }, options);
      this.addEventListener('focusin', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.userPaused = true;
          this.updatePlayback();
        }
      }, options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, options);
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); }
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const tab = event.target.closest('[data-story-tab]');
        if (!tab && !event.target.closest('.dx-stories__control')) return;
        const target = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (target === undefined) return;
        event.preventDefault();
        this.goTo(target, true);
        if (tab) this.tabs[this.index]?.focus({ preventScroll: true });
      }, options);
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || !event.target.closest('.dx-stories__visual')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, options);
      this.addEventListener('pointerup', (event) => {
        const start = this.pointerStart;
        this.pointerStart = undefined;
        if (!start || start.id !== event.pointerId) return;
        const x = event.clientX - start.x;
        const y = event.clientY - start.y;
        if (Math.abs(x) > 48 && Math.abs(x) > Math.abs(y) * 1.4) {
          this.ignoreClickUntil = performance.now() + 400;
          this.goTo(this.index + (x < 0 ? 1 : -1), true);
        }
      }, options);
      this.addEventListener('pointercancel', () => { this.pointerStart = undefined; }, options);
      this.addEventListener('click', (event) => {
        if (performance.now() < (this.ignoreClickUntil || 0) && event.target.closest('a')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => this.updatePlayback(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        this.schedule();
      }, { threshold: 0.2 });
      this.observer.observe(this);
      this.prepareFirstImage(options.signal);
      this.updatePlayback();
    }
    prepareFirstImage(signal) {
      const image = this.slides[this.index]?.querySelector('img');
      if (!image) {
        this.firstImageReady = true;
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled || signal.aborted) return;
        settled = true;
        const decoding = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
        Promise.resolve(decoding).catch(() => {}).then(() => {
          if (signal.aborted) return;
          this.firstImageReady = true;
          this.schedule();
        });
      };
      if (image.complete) {
        finish();
      } else {
        image.addEventListener('load', finish, { once: true, signal });
        image.addEventListener('error', finish, { once: true, signal });
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
    goTo(index, manual = false) {
      const next = (index + this.slides.length) % this.slides.length;
      if (manual) this.userPaused = true;
      const oldSlide = this.slides[this.index];
      if (oldSlide.contains(document.activeElement)) {
        this.querySelector('[data-story-next]').focus({ preventScroll: true });
      }
      this.index = next;
      const activeImage = this.slides[next].querySelector('img');
      if (activeImage) activeImage.loading = 'eager';
      this.track.setAttribute('aria-live', manual ? 'polite' : 'off');
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        const image = slide.querySelector('img');
        if (image) image.fetchPriority = active || position === 0 ? 'high' : 'low';
      });
      this.tabs.forEach((tab, position) => {
        const active = position === this.index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelector('[data-story-count]').textContent = String(this.index + 1).padStart(2, '0');
      this.querySelector('[data-story-current]').textContent = this.tabs[this.index]?.lastElementChild?.textContent || '';
      this.updatePlayback();
    }
    updatePlayback() {
      const paused = this.userPaused || this.motion.matches;
      this.toggle.hidden = this.motion.matches;
      this.toggle.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.querySelector('[data-story-play-icon]').toggleAttribute('hidden', !paused);
      this.querySelector('[data-story-pause-icon]').toggleAttribute('hidden', paused);
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.firstImageReady || this.userPaused || this.motion.matches || this.hovered || !this.onScreen || document.hidden) return;
      const nextIndex = (this.index + 1) % this.slides.length;
      const nextImage = this.slides[nextIndex].querySelector('img');
      if (nextImage) {
        nextImage.fetchPriority = nextIndex === 0 ? 'high' : 'low';
        nextImage.loading = 'eager';
      }
      this.timer = setTimeout(() => this.goTo(this.index + 1), 6500);
    }
  });
}
